<template>
  <div
    v-if="shouldDisplay"
    ref="root"
    class="border-static-info-mid rounded-lg flex flex-col gap-16 border-1 py-24"
  >
    <div class="flex items-center justify-between px-16 md:px-24">
      <div class="text-static-info-hi body-1-bold flex gap-4">
        <IconExport />
        <span>{{ i18n(translations.replaceWith) }}</span>
      </div>

      <ScrollNavigation
        :current-item
        :items-count="replacements.length"
        :loading
        @next="next"
        @previous="previous"
      />
    </div>

    <ScrollWrapper :current-item>
      <ReplacementItem
        v-for="replacement in replacements"
        :key="replacement.listingId"
        :category-id
        class="w-full shrink-0 px-16 md:px-24"
        :loading
        :mobile-plan
        :original
        :replacement
        @replace="() => replace(replacement)"
      />
    </ScrollWrapper>
  </div>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, ref, watch } from 'vue'

import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-b2c-services/mobile-plan/types/mobile-plan-offers'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { IconExport } from '@ds/icons/IconExport'
import { useIntersectionObserver } from '@vueuse/core'

import type { ReplacementOption } from '~/api/checkout/cart/types'

import translations from './Replacement.translations'
import ReplacementItem from './components/ReplacementItem/ReplacementItem.vue'
import ScrollNavigation from './components/ScrollNavigation/ScrollNavigation.vue'
import ScrollWrapper from './components/ScrollWrapper/ScrollWrapper.vue'

const props = defineProps<{
  mobilePlan?: { selectedOffer: MobilePlanOffer } | null
  categoryId: number
  original: ReplacementOption
  replacements: Array<ReplacementOption>
}>()

const emit = defineEmits(['replacing', 'replaced'])

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const logger = useLogger()
const { trackBlockImpression, trackClick, trackAddToCart, trackErrors } =
  useTracking()
const { openErrorToast } = useTheToast()

const currentItem = ref(0)
const loading = ref(false)
const root = ref<HTMLElement | null>()

const shouldDisplay = computed(() => {
  if (props.replacements.length === 0) {
    return false
  }

  return true
})

function previous() {
  currentItem.value -= 1
}

function next() {
  currentItem.value += 1
}

function trackImpression() {
  if (!shouldDisplay.value) {
    return
  }

  const newItem = props.replacements[currentItem.value]
  let price: 'lower' | 'same' | 'higher' = 'same'

  if (
    parseInt(props.original.price.amount, 10) <
    parseInt(newItem.price.amount, 10)
  ) {
    price = 'higher'
  } else if (
    parseInt(props.original.price.amount, 10) >
    parseInt(newItem.price.amount, 10)
  ) {
    price = 'lower'
  }

  trackBlockImpression({
    name: 'cart_item_alternative',
    index: currentItem.value + 1,
    product_price: newItem.price.amount,
    product_variant: newItem.grade.value,
    offer_type: newItem.offerType.value,
    is_grade_identical: props.original.grade.value === newItem.grade.value,
    is_offer_type_identical:
      props.original.offerType.value === newItem.offerType.value,
    product_replacement_range: price,
    // We don't have those informations in the in-store flow. We should add them in
    // the replacement endpoint: https://github.com/BackMarket/checkout/pull/566
    // product_category: props.item.category,
    // product_model: props.item.model,
    // product_id: props.item.akeneoId,
  })
}

watch(currentItem, trackImpression, { immediate: false })

const { stop } = useIntersectionObserver(root, ([element], observer) => {
  if (element.isIntersecting) {
    trackImpression()

    observer.unobserve(element.target)
  }
})

onUnmounted(() => {
  stop()
})

async function replace(replacement: ReplacementOption) {
  try {
    emit('replacing')
    loading.value = true

    trackClick({
      zone: 'cart',
      name: 'choose_replacement',
      index: currentItem.value + 1,
    })

    // We cannot parallelize those calls due to race conditions
    await $httpFetch('/bm/cart/add_product', {
      method: 'POST',
      body: {
        listing_id: replacement.listingId,
      },
    })

    await $httpFetch('/bm/cart/update_quantity', {
      method: 'POST',
      body: {
        action: 'delete',
        listing_id: `${props.original.listingId}`,
        listing_price: parseInt(props.original.price.amount, 10),
        new_quantity: 0,
      },
    })

    if (props.mobilePlan?.selectedOffer) {
      await $httpFetch('/bm/cart/services', {
        method: 'POST',
        body: {
          type: 'MOBILE_PLAN',
          listingPublicId: replacement.listingPublicId,
          offerId: props.mobilePlan?.selectedOffer.id,
        },
      })
    }

    trackAddToCart({
      list: 'cart_item_alternative',
      category: props.categoryId,
      listingId: replacement.listingId,
      name: replacement.title,
      price: replacement.price.amount,
      currency: replacement.price.currency,
      merchantId: replacement.sellerPublicId,
      position: currentItem.value + 1,
      quantity: 1,
      isOutOfStock: false,
      variant: replacement.grade.value,
      specialOfferType: replacement.offerType.value,
      // We don't have those informations in the in-store flow. We should add them in
      // the replacement endpoint: https://github.com/BackMarket/checkout/pull/566
      // id: props.item.productId,
      // uuid: props.item.akeneoId,
      // brand: props.item.brand,
    })
  } catch (error) {
    logger.error(
      '[CHECKOUT][OOS Alternative] An error occured while replacing a product',
      {
        owners: ['bot-squad-checkout-front'],
        error: error as unknown as Error,
      },
    )

    trackErrors({
      zone: 'oos_replacement',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      message: (error as any)?.payload?.detail ?? 'unknown_error',
    })

    openErrorToast()
  } finally {
    // Let's refresh the cart nonetheless
    emit('replaced', replacement.listingPublicId)
    loading.value = false
  }
}
</script>
